
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { AuthContext, Banner, Header, Footer, INavLink, INavPermissions, IUser, Nav, NotificationPopup, SettingsDialog } from '@sagecentral/core-ui';
import { AccountLinks } from 'models/accounts';
import { useMyPreferences, useNavLinks, usePreferenceOptions, useUpdateUserPreferences } from 'hooks/accounts';
import { useMaintenanceInfo } from 'hooks/system';
import NotificationsService, { INotificationMessage } from 'services/notifications/notifications-service';
import UserService, { IUserProfile } from 'services/users/users-service';
import { useFeatureEnabled } from 'hooks/shared';

interface ILayoutProps {
    children?: ReactNode
}


const routeMap = {
    public_tasks_my: { type: 'react', path: process.env.REACT_APP_NAV_LINK_MY_TASK },
    public_projects_my: { type: 'react', path: process.env.REACT_APP_NAV_LINK_MY_PROJECTS },
    public_projects_all: { type: 'react', path: process.env.REACT_APP_NAV_LINK_ALL_PROJECTS },
    public_my_team: { type: 'react', path: process.env.REACT_APP_NAV_LINK_MY_TEAM },
    public_workitems_all: { type: 'react', path: process.env.REACT_APP_NAV_LINK_ALL_WORKITEMS },
    public_my_workitems: { type: 'react', path: process.env.REACT_APP_NAV_LINK_MY_WORKITEMS },
    admin_accounts: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK_ADMIN_ACCOUNTS },
    admin_workspace: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK_ADMIN_WORKSPACE },
    admin_workspace_instances: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK_ADMIN_WORKSPACE_INSTANCE },
    admin_workflow: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK_ADMIN_WORKFLOW },
    admin_tasks: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK_ADMIN_TASKS },
    admin_metadata: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK__ADMIN_METADATA },
    admin_gl_code_mapping: { type: 'mvc', path: process.env.REACT_APP_NAV_LINK_ADMIN_GL_CODE_MAPPING },
    public_create: { type: 'react', path: process.env.REACT_APP_NAV_LINK_CREATE },
};
const features = { Help: false, MyTime: false, Search: false, Settings: { Dashboard: false } };
const suffixToRemove: string = '/in-progress';

const Layout: React.FunctionComponent<ILayoutProps> =
    ({ children, ...props }) => {
        const navigate = useNavigate();
        const auth = useContext(AuthContext);
        const [announcement, setAnnouncement] = useState<string | undefined>("");
        const [user, setUser] = useState<IUser>({} as IUser);
        const [userProfile, setUserProfile] = useState<IUserProfile>({ displayName: '', initials: '', userId: '' });
        const [notificationsCount, setNotificationsCount] = useState(0);
        const [accountLinks, setAccountLinks] = useState<AccountLinks>({ hasAdminLinks: false, linkNames: [] });
        const [notificationsToggle, setNotificationsToggler] = useState(false);
        const [settingsToggle, setSettingsToggler] = useState(false);
        const [drawerToggleStatus, setDrawerToggleStatus] = useState(false);
        const [ wave4Enabled ] = useFeatureEnabled('wave-4');
        const [navLinkPermissions, setNavLinkPermissions] = useState<INavPermissions>({
            hasAdminAccess: false,
            hasAccountAdminAccess: false,
            hasWorkspaceAdminAccess: false,
            hasWorkflowAdminAccess: false,
            hasTasksAdminAccess: false,
            hasMetadataAdminAccess: false,
            hasGLCodeMappingAdminAccess: false,
            hasMyTasksAccess: false,
            hasMyProjectsAccess: false,
            hasAllProjectsAccess: false,
            hasAllworkItemsAccess: false,
            hasMyTeamAccess: false,
            hasMyWorkitemsAccess: false,
            hasCreateAccess: false
        });
        const [accountLinksResult] = useNavLinks();
        const [refreshMyPreferences, setRefreshMyPreferences] = useState(true);
        const [myPreferencesResult, loadingMyPreferences] = useMyPreferences(refreshMyPreferences);
        const [preferenceOptionsResult, loadingPreferenceOptions] = usePreferenceOptions();
        const updateUserPreferences = useUpdateUserPreferences();
        const [systemMaintenanceInfoResult] = useMaintenanceInfo();

        const currentURL: string = window.location.href;
        const navLinks = [[
            {
                img: 'tasks',
                title: 'my tasks',
                text: 'My Tasks',
                route: 'public_tasks_my',
                collapsed: true,
                open: true,
                selected: routeMap.public_tasks_my.path ? 
                (currentURL.toLowerCase().includes(routeMap.public_tasks_my.path!.replace(suffixToRemove, '').toLowerCase())
                    || currentURL.toLowerCase().includes('tasks/')
                ) : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions?.hasMyTasksAccess || false;
                }
            },
            {
                img: 'workitems_my',
                title: 'my work items',
                text: 'My Work Items',
                route: 'public_my_workitems',
                collapsed: true,
                open: true,
                selected: routeMap.public_my_workitems.path ? currentURL.toLowerCase().includes(routeMap.public_my_workitems.path!.replace(suffixToRemove, '').toLowerCase()) : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions?.hasMyWorkitemsAccess || false;
                }
            },
            {
                img: 'workitems',
                title: 'all work items',
                text: 'All Work Items',
                route: 'public_workitems_all',
                collapsed: true,
                open: true,
                selected: routeMap.public_workitems_all.path ? currentURL.toLowerCase().includes(routeMap.public_workitems_all.path!.replace(suffixToRemove, '').toLowerCase()) : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions?.hasAllworkItemsAccess || false;
                }
            },
            {
                img: 'folder',
                title: 'my projects',
                text: 'My Projects',
                route: 'public_projects_my',
                collapsed: true,
                open: true,
                selected: routeMap.public_projects_my.path ? currentURL.toLowerCase().includes(routeMap.public_projects_my.path!.replace(suffixToRemove, '').toLowerCase()) : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions?.hasMyProjectsAccess || false;
                }
            },
            {
                img: 'projects',
                title: 'all projects',
                text: 'All Projects',
                route: 'public_projects_all',
                collapsed: true,
                open: true,
                selected: routeMap.public_projects_all.path ? currentURL.toLowerCase().includes(routeMap.public_projects_all.path!.replace(suffixToRemove, '').toLowerCase()) : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions?.hasAllProjectsAccess || false;
                }
            },

            {
                img: 'team',
                title: 'my team',
                text: 'My Team',
                route: 'public_my_team',
                collapsed: true,
                open: true,
                selected: routeMap.public_my_team.path ? currentURL.toLowerCase().includes(routeMap.public_my_team.path!.replace(suffixToRemove, '').toLowerCase()) : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions.hasMyTeamAccess || false;
                }
            },
            {
                img: 'create',
                title: 'create',
                text: 'Create',
                route: 'public_create',
                collapsed: true,
                open: true,
                selected: routeMap.public_create?.path 
                    ? (
                        currentURL.toLowerCase().includes(`${routeMap.public_create.path!.replace(suffixToRemove, '').toLowerCase()}/`) || 
                        currentURL.toLowerCase().endsWith(`${routeMap.public_create.path!.replace(suffixToRemove, '').toLowerCase()}`)
                    ) 
                    : false,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions.hasCreateAccess || false;
                }
            },
        ],
        [
            {
                img: 'key',
                title: 'admin',
                text: 'Admin',
                collapsed: true,
                open: true,
                hasPermissions: (permissions: INavPermissions) => {
                    return permissions?.hasAdminAccess || false;
                },
                links: [
                    {
                        img: '',
                        title: 'accounts',
                        text: 'Accounts',
                        route: 'admin_accounts',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_accounts.path ? currentURL.toLowerCase().includes(routeMap.admin_accounts.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasAccountAdminAccess || false;
                        }
                    },
                    {
                        img: '',
                        title: 'workspace',
                        text: 'Workspace',
                        route: 'admin_workspace',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_workspace.path ? currentURL.toLowerCase().includes(routeMap.admin_workspace.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasWorkspaceAdminAccess || false;
                        }
                    },
                    {
                        img: '',
                        title: 'workspace_instances',
                        text: 'Workspace Instances',
                        route: 'admin_workspace_instances',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_workspace_instances.path ? currentURL.toLowerCase().includes(routeMap.admin_workspace_instances.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasWorkspaceAdminAccess || false;
                        }
                    },
                    {
                        img: '',
                        title: 'workflow',
                        text: 'Workflow',
                        route: 'admin_workflow',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_workflow.path ? currentURL.toLowerCase().includes(routeMap.admin_workflow.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasWorkflowAdminAccess || false;
                        }
                    },
                    {
                        img: '',
                        title: 'tasks',
                        text: 'Tasks',
                        route: 'admin_tasks',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_tasks.path ? currentURL.toLowerCase().includes(routeMap.admin_tasks.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasTasksAdminAccess || false;
                        }
                    },
                    {
                        img: '',
                        title: 'metadata',
                        text: 'Metadata',
                        route: 'admin_metadata',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_metadata.path ? currentURL.toLowerCase().includes(routeMap.admin_metadata.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasMetadataAdminAccess || false;
                        }
                    },
                    {
                        img: '',
                        title: 'gl code mapping',
                        text: 'GL Code Mapping',
                        route: 'admin_gl_code_mapping',
                        collapsed: true,
                        open: true,
                        selected: routeMap.admin_gl_code_mapping.path ? currentURL.toLowerCase().includes(routeMap.admin_gl_code_mapping.path!.toLowerCase()) : false,
                        hasPermissions: (permissions: INavPermissions) => {
                            return permissions?.hasGLCodeMappingAdminAccess || false;
                        }
                    }
                ]
            }
        ]] as INavLink[][];

        useEffect(() => {
            const readUser = async () => {
                const user = await auth.getUser() as IUser;
                setUser(user);
            };

            readUser();
        }, []);

        useEffect(() => {
            if (user && user.id_token) {
                (async () => {
                    const count = await NotificationsService.getNotificationsCount(user.id_token);
                    setNotificationsCount(count);
                })();
            }
        }, [user]);

        useEffect(() => {
            if (user && user.id_token) {
                (async () => {
                    const profile = await UserService.getProfile(user.id_token);
                    setUserProfile(profile);
                })();
            }
        }, [user]);

        useEffect(() => {
            if (accountLinksResult) {
                var hasAccountAdminAccess = accountLinksResult?.linkNames?.indexOf('accounts') > -1 || false;
                var hasWorkspaceAdminAccess = accountLinksResult?.linkNames?.indexOf('workspace') > -1 || false;
                var hasWorkflowAdminAccess = accountLinksResult?.linkNames?.indexOf('workflow') > -1 || false;
                var hasTasksAdminAccess = accountLinksResult?.linkNames?.indexOf('tasks') > -1 || false;
                var hasMetadataAdminAccess = accountLinksResult?.linkNames?.indexOf('metadata') > -1 || false;
                var hasGLCodeMappingAdminAccess = accountLinksResult?.linkNames?.indexOf('glCodeMapping') > -1 || false;
                var hasAdminAccess = (accountLinksResult?.hasAdminLinks && 
                                    (hasAccountAdminAccess || 
                                    hasWorkspaceAdminAccess || 
                                    hasWorkflowAdminAccess ||
                                    hasTasksAdminAccess ||
                                    hasMetadataAdminAccess || 
                                    hasGLCodeMappingAdminAccess)) || false; 
                setAccountLinks(accountLinksResult);
                setNavLinkPermissions({
                    hasAdminAccess: hasAdminAccess,
                    hasAccountAdminAccess: hasAccountAdminAccess, 
                    hasWorkspaceAdminAccess: hasWorkspaceAdminAccess,
                    hasWorkflowAdminAccess: hasWorkflowAdminAccess, 
                    hasTasksAdminAccess: hasTasksAdminAccess, 
                    hasMetadataAdminAccess: hasMetadataAdminAccess, 
                    hasGLCodeMappingAdminAccess: hasGLCodeMappingAdminAccess, 
                    hasMyTasksAccess: accountLinksResult?.linkNames?.indexOf('myTasks') > -1 || false,
                    hasMyProjectsAccess: accountLinksResult?.linkNames?.indexOf('myProjects') > -1 || false,
                    hasAllProjectsAccess: accountLinksResult?.linkNames?.indexOf('allProjects') > -1 || false,
                    hasMyTeamAccess: accountLinksResult?.linkNames?.indexOf('myTeam') > -1 || false,
                    hasAllworkItemsAccess: accountLinksResult?.linkNames?.indexOf('allItems') > -1 || false,
                    hasCreateAccess: accountLinksResult?.linkNames?.indexOf('create') > -1 || false,
                    hasMyWorkitemsAccess: true,
                });
            }
        }, [accountLinksResult]);

        useEffect(() => {
            (async () => {
                if (user && user.id_token) {
                    await accountLinksResult;
                };
            })();
        }, [user, accountLinksResult]);

        useEffect(() => {
            (async () => {
                if (user && user.id_token) {
                    setAnnouncement(await systemMaintenanceInfoResult?.message);
                }
            })();
        }, [user, systemMaintenanceInfoResult]);

        const handleNavItemClick =
            (e: any, route?: string) => {
                if (!!route) {
                    var map = routeMap[route as keyof typeof routeMap];
                    if (isClientRoute(`${map.type}`)) {
                        navigate(`${map.path}`);
                    }
                    else {
                        navigate(`external?url=${map.path}`);
                    }
                }
            }

        const handleNavToggle =
            (drawerToggle: boolean) => {
                setDrawerToggleStatus(drawerToggle);
            }

        const handleLogoutClick =
            () => {
                auth.logout();
            }

        const handleNotificationsToggler =
            () => {
                setNotificationsToggler(!notificationsToggle);
            }

        const handleSettingsToggler =
            () => {
                setSettingsToggler(!settingsToggle);
                if (settingsToggle) {
                    setRefreshMyPreferences(true);
                }
            }

        const isClientRoute =
            (type: string): boolean => {
                return (type.indexOf('react') === 0);
            }

        const getNotificationMessages = async (readAllNotifications: boolean) => {
            if (user && user.id_token && user.id_token.length > 0) {
                var messages = await NotificationsService.getNotificationsMessages(user.id_token, readAllNotifications);
                return messages;
            }
            return [];
        }

        const onSetAllNotificationsAsRead = () => {
            if (user && user.id_token && user.id_token.length > 0) {
                NotificationsService.setAllNotificationsAsRead(user.id_token);
            }
        }

        const getPreferenceOptions = async () => {
            if (user && user.id_token && user.id_token.length > 0) {
                return preferenceOptionsResult;
            }
        }
        const getMyPreferences = async () => {
            if (user && user.id_token && user.id_token.length > 0) {
                const preferences = await myPreferencesResult;
                return preferences;
            }
        }
        const onUpdateUserPreferences = async (preferences: any) => {
            if (user && user.id_token && user.id_token.length > 0) {
                setRefreshMyPreferences(false);
                return await updateUserPreferences(preferences);
            }
        }

        const onSetNotificationsCount = (count: number) => {
            setNotificationsCount(count);
        }

        const onCloseNotification = () => {
            setNotificationsToggler(!notificationsToggle);
        }
        

        console.log(navLinks);
        console.log(navLinkPermissions);

        return (
            <Box sx={{
                display: 'flex',
                border: 0,
                '& .MuiListItemText-primary': {
                    color: '#fff'
                },                
            }}><Nav
                    open={drawerToggleStatus}
                    adminExpanded={true}
                    permissions={navLinkPermissions}
                    links={navLinks}
                    onNavItemClick={handleNavItemClick}
                    onNavToggle={handleNavToggle}></Nav>
                <Box sx={{
                    flexGrow: 1, bgcolor: 'background.default', p: 0, mt: 0, overflowX: "auto",
                    display: 'flex', flexDirection: 'column', height: '100vh'
                }}>
                    <Header
                        open={drawerToggleStatus}
                        features={features}
                        drawerWidth={240}
                        drawerClollapsedWidth={72}
                        userFullName={userProfile?.displayName}
                        userInitials={`${userProfile?.initials}`}
                        onProfileClick={() => { window.location.replace(process.env.REACT_APP_CLASSIC_URL + '/ExternalResource/ManagemyProfile?area=ExternalResourceManagement') }}
                        onLogoutClick={handleLogoutClick}
                        hasNotificationsPermission={accountLinks.linkNames.indexOf("notifications") > -1}
                        hasSettingsPermission={accountLinks.linkNames.indexOf("settings") > -1}
                        notificationsCount={notificationsCount}
                        onNotificationClick={handleNotificationsToggler}
                        onSettingsClick={handleSettingsToggler}
                        notificationElement={<NotificationPopup<INotificationMessage>
                            open={notificationsToggle}
                            allNotifications={false}
                            onSetNotificationsCount={onSetNotificationsCount}
                            onSetAllNotificationsAsRead={onSetAllNotificationsAsRead}
                            getNotificationMessages={getNotificationMessages}
                            onCloseNotification={onCloseNotification}
                            wave4Enabled={wave4Enabled}
                        />}
                        settingsElement={!loadingMyPreferences && !loadingPreferenceOptions ? <SettingsDialog
                            open={settingsToggle}
                            handleNotificationsToggler={handleSettingsToggler}
                            getPreferenceOptions={getPreferenceOptions}
                            getMyPreferences={getMyPreferences}
                            onUpdateUserPreferences={onUpdateUserPreferences}
                            displayDashboardSettings={features?.Settings?.Dashboard ? features?.Settings?.Dashboard : false}
                        /> : <></>}
                    ></Header>
                    {!!announcement && announcement.length > 0 &&
                        <Banner
                            message={announcement}
                            dismissable={true} />}
                    <Box sx={{ p: 3, flexGrow: 1 }} component="main" aria-label="main content">
                        {children}
                    </Box>
                    <Footer
                        version={process.env.REACT_APP_VERSION}
                        staticUrl={process.env.REACT_APP_CLASSIC_URL}
                    ></Footer>
                </Box>
            </Box>
        )
    }

export default Layout